import type { ImageTransformOptions } from '@/lib/cdn';

/** Matches `styles/tokens/_breakpoints.scss` `$breakpoint-md`. */
export const HERO_THUMBNAIL_BREAKPOINT = 768;

/** Portrait thumbnail slot sizes for Dynamic Hero — single source of truth for CSS + `<Image>`. */
export const HERO_THUMBNAIL_DIMENSIONS = {
  mobile: { width: 131, height: 200 },
  desktop: { width: 230, height: 350 },
} as const;

export type HeroThumbnailDimensions =
  (typeof HERO_THUMBNAIL_DIMENSIONS)[keyof typeof HERO_THUMBNAIL_DIMENSIONS];

/** CSS custom properties consumed by `.thumbnail` in HeroSlider.module.scss */
export const HERO_THUMBNAIL_CSS_VARS = {
  width: '--hero-thumb-width',
  height: '--hero-thumb-height',
  widthMd: '--hero-thumb-width-md',
  heightMd: '--hero-thumb-height-md',
} as const;

export function getHeroThumbnailCssVars(): Record<string, string> {
  const { mobile, desktop } = HERO_THUMBNAIL_DIMENSIONS;

  return {
    [HERO_THUMBNAIL_CSS_VARS.width]: `${mobile.width}px`,
    [HERO_THUMBNAIL_CSS_VARS.height]: `${mobile.height}px`,
    [HERO_THUMBNAIL_CSS_VARS.widthMd]: `${desktop.width}px`,
    [HERO_THUMBNAIL_CSS_VARS.heightMd]: `${desktop.height}px`,
  };
}

/** `sizes` hints for Next.js `<Image fill />` in fixed-size thumbnail slots. */
export const HERO_THUMBNAIL_SIZES = {
  mobileOnly: `(max-width: ${HERO_THUMBNAIL_BREAKPOINT - 1}px) ${HERO_THUMBNAIL_DIMENSIONS.mobile.width}px, 0px`,
  desktopOnly: `(min-width: ${HERO_THUMBNAIL_BREAKPOINT}px) ${HERO_THUMBNAIL_DIMENSIONS.desktop.width}px, 0px`,
  responsive: `(min-width: ${HERO_THUMBNAIL_BREAKPOINT}px) ${HERO_THUMBNAIL_DIMENSIONS.desktop.width}px, ${HERO_THUMBNAIL_DIMENSIONS.mobile.width}px`,
} as const;

/** `sizes` hints for Dynamic Hero full-width background `<Image fill />`. */
export const HERO_BACKGROUND_SIZES = {
  mobileOnly: `(max-width: ${HERO_THUMBNAIL_BREAKPOINT - 1}px) 100vw, 0px`,
  desktopOnly: `(min-width: ${HERO_THUMBNAIL_BREAKPOINT}px) 100vw, 0px`,
  /** Single asset — oversized mobile hint for cover-crop sharpness. */
  responsive: `(max-width: ${HERO_THUMBNAIL_BREAKPOINT - 1}px) 200vw, 100vw`,
} as const;

type CdnLoader = (props: ImageTransformOptions & { src: string }) => string;

type NextImageLoader = (props: { src: string; width: number; quality?: number }) => string;

/** CDN loader that crops hero media to the portrait thumbnail aspect ratio. */
export function createHeroThumbnailLoader(
  loader: CdnLoader,
  aspectRatio: HeroThumbnailDimensions,
  focalPoint: { x: number; y: number } | null | undefined,
  originalImageDimensions: { x: number; y: number } | undefined
): NextImageLoader {
  return ({ src, width, quality }) =>
    loader({
      src,
      width,
      height: Math.round(width * (aspectRatio.height / aspectRatio.width)),
      quality,
      format: 'auto',
      fit: 'cover',
      aspectRatioWidth: aspectRatio.width,
      aspectRatioHeight: aspectRatio.height,
      ...(focalPoint ? { focalPoint, originalImageDimensions } : {}),
    });
}
